import { create } from "zustand";
import { persist } from "zustand/middleware";
import useChatStore from "./chatStore";

const API_BASE = import.meta.env.VITE_API_URL;

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      organizations: [],
      projects: [],
      currentProject: null,
      selectedNote: null,
      _hasHydrated: false,
      _justLoggedIn: false, // Flag to skip auth check right after login

      login: async (email, password) => {
        try {
          const res = await fetch(`${API_BASE}/api/auth/login`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ email, password }),
          });

          if (!res.ok) {
            const { message } = await res.json();
            return { success: false, message };
          }

          const { token, user, organizations, projects, currentProject } =
            await res.json();

          set({
            token,
            user,
            organizations,
            projects,
            currentProject,
            _justLoggedIn: true, // Skip next auth check
          });

          return { success: true };
        } catch (err) {
          console.error("Login failed:", err);
          return { success: false, message: "Login failed" };
        }
      },

      logout: () => {
        // Clear chat data when logging out
        useChatStore.getState().clearChatData();

        set({
          token: null,
          user: null,
          organizations: [],
          projects: [],
          currentProject: null,
          selectedNote: null,
        });
      },

      signup: async (email, password) => {
        try {
          const res = await fetch(`${API_BASE}/api/auth/signup`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ email, password }),
          });

          if (!res.ok) {
            const { message } = await res.json();
            return { success: false, message };
          }

          return { success: true };
        } catch (err) {
          return { success: false, message: "Signup failed" };
        }
      },

      checkAuth: async () => {
        const token = get().token;
        if (!token) return;

        // Skip auth check if we just logged in (data is fresh)
        if (get()._justLoggedIn) {
          set({ _justLoggedIn: false });
          return;
        }

        try {
          const res = await fetch(`${API_BASE}/api/auth/me`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (!res.ok) {
            // Any auth failure should clear state and redirect to login
            get().logout();
            // Only redirect if not already on login/signup pages
            const currentPath = window.location.hash;
            if (
              !currentPath.includes("/login") &&
              !currentPath.includes("/signup") &&
              !currentPath.includes("/accept-invite")
            ) {
              window.location.href = "/#/login";
            }
            return;
          }

          const { user, organizations, projects, currentProject } =
            await res.json();

          set({
            user,
            organizations,
            projects,
            currentProject,
          });
        } catch {
          // Network error or other failure - logout and redirect
          get().logout();
          const currentPath = window.location.hash;
          if (
            !currentPath.includes("/login") &&
            !currentPath.includes("/signup") &&
            !currentPath.includes("/accept-invite")
          ) {
            window.location.href = "/#/login";
          }
        }
      },

      // Refresh user data without redirect logic (for invite acceptance)
      refreshUserData: async () => {
        const token = get().token;
        if (!token) return false;

        try {
          const res = await fetch(`${API_BASE}/api/auth/me`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (!res.ok) {
            console.error("Failed to refresh user data, status:", res.status);
            return false;
          }

          const { user, organizations, projects, currentProject } =
            await res.json();

          set({
            user,
            organizations,
            projects,
            currentProject,
          });

          return true;
        } catch (err) {
          console.error("Error refreshing user data:", err);
          return false;
        }
      },

      fetchProjects: async () => {
        const user = get().user;
        const token = get().token;
        const userId = user?._id || user?.id;
        const organizationId = user?.currentOrganization;

        if (!userId || !token) {
          return;
        }

        try {
          let url = `${API_BASE}/api/projects?userId=${userId}`;

          // Add organizationId filter if available
          if (organizationId) {
            url += `&organizationId=${organizationId}`;
          }

          const res = await fetch(url, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (!res.ok) {
            throw new Error("Failed to fetch projects");
          }

          const projects = await res.json();

          // Update currentProject if it exists in the fresh projects
          const currentProject = get().currentProject;
          if (currentProject) {
            const updatedCurrentProject = projects.find(
              (p) => p._id === currentProject._id || p.id === currentProject.id,
            );
            if (updatedCurrentProject) {
              set({ projects, currentProject: updatedCurrentProject });
            } else {
              set({ projects });
            }
          } else {
            set({ projects });
          }

          return projects;
        } catch (error) {
          console.error("Error fetching projects:", error);
          return [];
        }
      },

      fetchOrganizations: async () => {
        const token = get().token;
        if (!token) {
          return [];
        }

        try {
          const res = await fetch(`${API_BASE}/api/organizations`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (!res.ok) {
            const errorText = await res.text();
            console.error(
              "Failed to fetch organizations:",
              res.status,
              errorText,
            );

            // If unauthorized, logout and redirect
            if (res.status === 401) {
              get().logout();
              window.location.href = "/#/login";
            }

            throw new Error("Failed to fetch organizations");
          }

          const organizations = await res.json();

          set({ organizations });

          return organizations;
        } catch (error) {
          console.error("Error fetching organizations:", error);
          return [];
        }
      },

      setCurrentProject: async (project) => {
        // Ensure project has both id and _id fields for consistency
        const normalizedProject = project
          ? {
              ...project,
              id: project.id || project._id,
              _id: project._id || project.id,
            }
          : null;

        set({ currentProject: normalizedProject });

        // Persist the change to the server
        if (normalizedProject) {
          const token = get().token;
          if (token) {
            try {
              await fetch(`${API_BASE}/api/auth/switch-project`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ projectId: normalizedProject._id }),
              });
              // We don't need to update state from response since we already set it above
            } catch (error) {
              console.error(
                "Failed to persist project change to server:",
                error,
              );
              // Still keep the local change even if server update fails
            }
          }
        }
      },

      setSelectedNote: (note) => {
        set({ selectedNote: note });
      },

      updateUser: (updatedUser) => {
        set({ user: { ...get().user, ...updatedUser } });
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        organizations: state.organizations,
        projects: state.projects,
        currentProject: state.currentProject,
        selectedNote: state.selectedNote,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state._hasHydrated = true;
        }
      },
    },
  ),
);

export default useAuthStore;
